import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState, useCallback } from "react";

import axios from "../../api/axios";
import { registerSchema } from "../../schemas";
import InputField from "../../components/form/InputField";
import SelectField from "../../components/form/SelectField";
import AuthRedirect from "../../components/hoc/AuthRedirect";
import { registerUser, clearError } from "../../features/userSlice";
import {
  getRegisterCountryCommunities,
  unsetCommunity,
} from "../../features/communitiesSlice";
import { useNotification } from "../../components/notification/NotificationContainer";
import loadStatus from "../../util/loadStatus";

const Register = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const communities = useSelector((state) => state.communities.data);
  const [communityVisible, setCommunityVisible] = useState(false);
  const loadRegisterUser = useSelector((state) => state.user.load.registerUser);
  const addNotification = useNotification();
  const {
    register,
    watch,
    setValue,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      other_names: "",
      email: "",
      password: "",
      confirm_password: "",
    },
  });
  const { error } = useSelector((state) => state.user);
  const watchAllFields = watch();

  //Register user
  const onSubmit = async (data) => {
    try {
      let res = await dispatch(registerUser(data)).unwrap();
      if (registerUser.fulfilled.match(res)) {
        navigate("/dashboard");
        addNotification("Registration Successful", "success");
      } else {
        addNotification(res.payload.message, "error");
      }
    } catch (err) {
      addNotification(err.message, "waarning");
    }
  };

  //Get register organization, and roles
  const getRegister = useCallback(async () => {
    try {
      const response = await axios.get("/register");
      setRoles(response.data.roles);
      setOrganizations(response.data.organizations);
    } catch (error) {
      if (error.response) {
        addNotification(error.response.data.message, "error");
      } else {
        addNotification(error.message, "warning");
      }
    }
  }, [setRoles]);

  //Get Role Values
  useEffect(() => {
    getRegister();
    return () => {
      dispatch(clearError());
    };
  }, []);

  useEffect(() => {
    reset({ organization_id: "", role_id: "" });
  }, [roles]);

  useEffect(() => {
    let role;
    if (watchAllFields.role_id) {
      role = roles.find((item) => item.id === watchAllFields.role_id);
    }
    if (
      role &&
      role.name.toLowerCase() === "community cordinator" &&
      watchAllFields.organization_id
    ) {
      setValue("community_id", "");
      setCommunityVisible(true);
      dispatch(getRegisterCountryCommunities(watchAllFields.organization_id));
    } else {
      setValue("community_id", "");
      setCommunityVisible(false);
      dispatch(unsetCommunity());
    }
  }, [watchAllFields.role_id, watchAllFields.organization_id]);

  return (
    <>
      <AuthRedirect>
        <div className="auth-container">
          <div className="auth-content ">
            <div className=" auth-inner-content ">
              <p className="text-center text-red-500">{error?.message}</p>
              <h3 className="auth-title ">Register</h3>
              <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
                <div className="flex justify-center items-center gap-3 sm:flex-col">
                  <InputField
                    register={register}
                    errors={errors.first_name}
                    label="First Name"
                    name="first_name"
                    type="text"
                  />
                  <InputField
                    register={register}
                    errors={errors.last_name}
                    label="Last Name"
                    name="last_name"
                    type="text"
                  />
                </div>
                <InputField
                  register={register}
                  errors={errors.other_names}
                  label="Other Names"
                  name="other_names"
                  type="text"
                />
                <SelectField
                  register={register}
                  errors={errors.organization_id}
                  data={organizations}
                  name="organization_id"
                  label="Organization"
                />

                <SelectField
                  data={roles}
                  name="role_id"
                  errors={errors.role_id}
                  label="Role"
                  register={register}
                />
                {communityVisible ? (
                  <SelectField
                    register={register}
                    errors={errors.community_id}
                    data={communities}
                    name="community_id"
                    label="community"
                  />
                ) : (
                  <div className="flex-1"></div>
                )}
                <InputField
                  register={register}
                  errors={errors.email}
                  label="Email"
                  name="email"
                  placeholder="david@examplie.com"
                  type="email"
                  autoComplete="false"
                />
                <InputField
                  register={register}
                  errors={errors.password}
                  label="Password"
                  name="password"
                  placeholder="**********"
                  type="password"
                />
                <InputField
                  register={register}
                  errors={errors.confirm_password}
                  label="Confirm Password"
                  name="confirm_password"
                  placeholder="**********"
                  type="password"
                />
                <div className=" flex flex-col justify-center items-center gap-y-2 text-text-1/45">
                  <p className="text-xs leading-4 text-center">
                    By creating an account, you agree to our{" "}
                    <Link className="text-primary underline hover:text-primary/60">
                      Terms
                    </Link>{" "}
                    and have read and acknowledge the{" "}
                    <Link className="text-primary underline hover:text-primary/60">
                      Global Privacy Statement
                    </Link>
                  </p>
                  <button
                    type="submit"
                    className="auth-btn"
                    disabled={loadRegisterUser === loadStatus.loading}
                  >
                    Register
                  </button>
                  <p className="text-xs text-center">
                    Already have an account?{" "}
                    <Link
                      to="/login"
                      className="text-primary underline hover:text-primary/60"
                    >
                      Sign In
                    </Link>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </AuthRedirect>
    </>
  );
};

export default Register;
